/**
 * Module Catalog - Package-based System
 * 
 * 4 Main Packages:
 * - LeadHub: CRM features (Contacts, Companies, Sales Opportunities, Customers, Products, Offers, Closing, Success Metrics)
 * - LeadBoost: Marketing features (Brandbook, Status, Campaigns, Optimization)
 * - LeadInsights: Analytics features (Customer Analysis)
 * - Actions: Productivity features (Tasks, Stream, Emails, Time Tracking, Business Plan)
 */

export interface Module {
  id: string;
  name: string;
  description: string;
  price: number;
  required: boolean;
  industries: string[];  // Industries this module is recommended for
  features: string[];
  recommendationReason?: Record<string, string>;  // Reason per industry
  package?: 'lead_hub' | 'lead_boost' | 'lead_insights' | 'actions';  // Which package this belongs to
}

// Package definitions
export interface Package {
  id: string;
  name: string;
  description: string;
  price: number;
  features: string[];
  moduleIds: string[];  // Legacy module IDs included in this package
}

export const PACKAGES: Package[] = [
  {
    id: 'lead_hub',
    name: 'LeadHub',
    description: 'Komplettes CRM für Vertrieb und Kundenmanagement',
    price: 29,
    features: [
      'Kontakte & Lead Management',
      'Unternehmensverwaltung',
      'Verkaufschancen & Pipeline',
      'Kundendatenbank',
      'Produktkatalog',
      'Angebotserstellung',
      'Abschluss-Assistent',
      'Erfolgsmessung'
    ],
    moduleIds: ['crm_core']
  },
  {
    id: 'lead_boost',
    name: 'LeadBoost',
    description: 'Marketing-Automation mit Google Ads & Brand Management',
    price: 39,
    features: [
      'Brand Book & CI',
      'Google Ads Status',
      'Kampagnen-Management',
      'KI-Optimierung',
      'Performance Max',
      'Keyword Planner'
    ],
    moduleIds: ['google_ads']
  },
  {
    id: 'lead_insights',
    name: 'LeadInsights',
    description: 'Tiefgehende Kundenanalyse mit KI',
    price: 19,
    features: [
      'Kundenanalyse',
      'Website-Audit',
      'Wettbewerbsanalyse',
      'Ask Aideline KI'
    ],
    moduleIds: ['customer_intelligence']
  },
  {
    id: 'actions',
    name: 'Actions',
    description: 'Produktivitäts-Tools für den Arbeitsalltag',
    price: 19,
    features: [
      'Aufgabenverwaltung',
      'Team-Stream',
      'E-Mail-Integration',
      'Zeiterfassung',
      'Business Plan'
    ],
    moduleIds: ['time_tracking', 'business_planning']
  }
];

export const MODULES: Module[] = [
  {
    id: 'crm_core',
    name: 'LeadHub CRM',
    description: 'Kontakte, Unternehmen, Verkaufschancen, Pipeline Management',
    price: 29,
    required: true,  // Always activated
    industries: ['automotive', 'insurance', 'saas', 'ecommerce', 'agency', 'consulting', 'finance'],
    features: [
      'Kontakte & Leads',
      'Unternehmensverwaltung',
      'Verkaufschancen',
      'Pipeline Management',
      'Produktkatalog',
      'Angebote',
      'Abschluss-Assistent',
      'Erfolgsmessung'
    ],
    package: 'lead_hub'
  },
  
  {
    id: 'google_ads',
    name: 'LeadBoost Marketing',
    description: 'Google Ads Automation, Brandbook, Kampagnen-Optimierung',
    price: 39,
    required: false,
    industries: ['automotive', 'saas', 'ecommerce', 'agency'],
    features: [
      'Brand Book',
      'Google Ads Status',
      'Kampagnen-Management',
      'KI-Optimierung',
      'Performance Max',
      'Keyword Planner',
      'Budget-Optimierung'
    ],
    recommendationReason: {
      automotive: 'Automatisierte Werbekampagnen für Fahrzeugverkauf & Service',
      saas: 'User-Akquise mit gezielten Search- und Display-Ads automatisieren',
      ecommerce: 'Produkt-Verkäufe steigern mit automatisierten Shopping & PMax Kampagnen',
      agency: 'Eigene Agentur bewerben UND Klienten-Kampagnen verwalten'
    },
    package: 'lead_boost'
  },
  
  {
    id: 'customer_intelligence',
    name: 'LeadInsights Analyse',
    description: 'Tiefgehende Kundenanalyse und Wettbewerbsbeobachtung',
    price: 19,
    required: false,
    industries: ['saas', 'ecommerce', 'agency', 'consulting'],
    features: [
      'Kundenanalyse',
      'Website-Audit',
      'Wettbewerbsanalyse',
      'Ask Aideline',
      'Branchen-Insights'
    ],
    recommendationReason: {
      saas: 'Verstehe deine Kunden besser und optimiere dein Produkt',
      ecommerce: 'Analysiere Kundenverhalten und Wettbewerber',
      agency: 'Erstelle professionelle Kundenanalysen für Pitches',
      consulting: 'Datengetriebene Beratung mit KI-Unterstützung'
    },
    package: 'lead_insights'
  },
  
  {
    id: 'time_tracking',
    name: 'Zeiterfassung',
    description: 'Stunden erfassen, Projekte abrechnen',
    price: 9,
    required: false,
    industries: ['consulting', 'agency'],
    features: [
      'Stunden tracken',
      'Projekt-Zuordnung',
      'Team-Reports',
      'Billable vs Non-Billable'
    ],
    recommendationReason: {
      consulting: 'Consulting-Stunden erfassen und professionelle Rechnungen erstellen',
      agency: 'Klienten-Projekte abrechnen - Stunden tracken für Social Media, Ads, Content',
      automotive: 'Für Werkstatt-Abrechnung nützlich'
    },
    package: 'actions'
  },
  
  {
    id: 'business_planning',
    name: 'Business Plan',
    description: 'Umsatz-Prognosen und Finanzplanung',
    price: 9,
    required: false,
    industries: ['saas', 'consulting', 'ecommerce'],
    features: [
      'Cash Flow Forecast',
      'MRR/ARR Tracking',
      'Revenue Projections',
      'Subscription Analytics'
    ],
    recommendationReason: {
      saas: 'MRR/ARR Tracking, Churn-Analyse & Subscription Revenue Growth',
      consulting: 'Projekt-Umsätze forecasting und Cash Flow Management',
      ecommerce: 'Saisonale Umsatz-Prognosen und Inventory-Planung',
      default: 'Plan ahead with accurate revenue and cost projections'
    },
    package: 'actions'
  },
  
  {
    id: 'agent_mode',
    name: 'KI Voice-Assistent',
    description: 'Voice-powered CRM control, hands-free operation',
    price: 19,
    required: false,
    industries: [],  // Optional for all
    features: [
      'Voice Commands',
      'Smart Dictation',
      'Meeting Summaries',
      'Voice Search',
      'Hands-free Operation'
    ],
    recommendationReason: {
      default: 'CRM per Sprache steuern - perfekt für unterwegs'
    }
  },
  
  {
    id: 'mcp_integration',
    name: 'Externe Integrationen',
    description: 'Connect n8n, Zapier, custom AI tools, external APIs',
    price: 19,
    required: false,
    industries: ['saas'],  // Advanced users
    features: [
      'n8n Workflows',
      'Zapier Integration',
      'Custom AI Tools',
      'API Access',
      'Webhooks',
      'Custom Automations'
    ],
    recommendationReason: {
      saas: 'Integrate with your product and automate complex workflows',
      agency: 'Connect client tools and build custom automations'
    }
  }
];

/**
 * Get all available modules
 */
export function getAllModules(): Module[] {
  return MODULES;
}

/**
 * Get all packages
 */
export function getAllPackages(): Package[] {
  return PACKAGES;
}

/**
 * Get package by ID
 */
export function getPackageById(packageId: string): Package | undefined {
  return PACKAGES.find(p => p.id === packageId);
}

/**
 * Get module by ID
 */
export function getModuleById(moduleId: string): Module | undefined {
  return MODULES.find(m => m.id === moduleId);
}

/**
 * Get modules for a specific industry
 */
export function getModulesForIndustry(industry: string): {
  required: Module[];
  recommended: Module[];
  optional: Module[];
} {
  const required = MODULES.filter(m => m.required);
  const recommended = MODULES.filter(m => !m.required && m.industries.includes(industry));
  const optional = MODULES.filter(m => !m.required && !m.industries.includes(industry));
  
  return { required, recommended, optional };
}

/**
 * Get packages recommended for an industry
 */
export function getPackagesForIndustry(industry: string): Package[] {
  // All packages are generally recommended, but order by relevance
  const packageOrder: Record<string, string[]> = {
    automotive: ['lead_hub', 'lead_boost', 'lead_insights', 'actions'],
    saas: ['lead_hub', 'lead_insights', 'lead_boost', 'actions'],
    ecommerce: ['lead_hub', 'lead_boost', 'lead_insights', 'actions'],
    agency: ['lead_hub', 'lead_boost', 'actions', 'lead_insights'],
    consulting: ['lead_hub', 'lead_insights', 'actions', 'lead_boost'],
    insurance: ['lead_hub', 'lead_insights', 'actions', 'lead_boost'],
    finance: ['lead_hub', 'lead_insights', 'actions', 'lead_boost'],
  };
  
  const order = packageOrder[industry] || ['lead_hub', 'lead_boost', 'lead_insights', 'actions'];
  return order.map(id => PACKAGES.find(p => p.id === id)!).filter(Boolean);
}

/**
 * Calculate total monthly price for selected modules
 */
export function calculateTotalPrice(moduleIds: string[]): number {
  return moduleIds.reduce((total, id) => {
    const module = getModuleById(id);
    return total + (module?.price || 0);
  }, 0);
}

/**
 * Calculate total monthly price for selected packages
 */
export function calculatePackagePrice(packageIds: string[]): number {
  return packageIds.reduce((total, id) => {
    const pkg = getPackageById(id);
    return total + (pkg?.price || 0);
  }, 0);
}

/**
 * Get recommendation reason for a module in a specific industry
 */
export function getRecommendationReason(moduleId: string, industry: string): string {
  const module = getModuleById(moduleId);
  if (!module?.recommendationReason) return '';
  
  return module.recommendationReason[industry] || module.recommendationReason.default || '';
}
